/**
 * dominant-color.js
 *
 * Replaces the grey shimmer placeholder on `.lazy-image` wrappers with the
 * image's main colour, extracted at runtime from a tiny CDN-downsampled copy.
 *
 * How it works:
 *   1. Liquid (`snippets/image.liquid`, `snippets/image-fill.liquid`) stamps each
 *      wrapper with `data-color-src` pointing at a ~16px version of the same image.
 *   2. A single IntersectionObserver waits until a wrapper nears the viewport, so
 *      we never fetch ahead of Shopify's own lazy-loading.
 *   3. The tiny image is drawn to a 16x16 canvas, quantised into a coarse colour
 *      histogram, and the most representative bucket becomes `--placeholder-color`.
 *   4. CSS (`theme.css`, `.lazy-image.has-color`) swaps the shimmer for that colour;
 *      the full image then fades in on top via the existing `is-loading` flow.
 *
 * Any failure (CORS, decode error) is swallowed — the wrapper keeps the original
 * shimmer, so this is purely additive.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var CACHE_PREFIX = 'dc:'; // sessionStorage key prefix, keyed by tiny-image URL
  var CANVAS_SIZE = 16;
  var SELECTOR = '.lazy-image[data-color-src]:not([data-color-done])';

  var canvas = document.createElement('canvas');
  canvas.width = canvas.height = CANVAS_SIZE;
  // willReadFrequently keeps repeated getImageData on the CPU path (faster here).
  var ctx = canvas.getContext('2d', { willReadFrequently: true });

  function readCache(url) {
    try { return sessionStorage.getItem(CACHE_PREFIX + url); } catch (e) { return null; }
  }
  function writeCache(url, hex) {
    try { sessionStorage.setItem(CACHE_PREFIX + url, hex); } catch (e) { /* quota / private mode */ }
  }

  function apply(el, hex) {
    el.style.setProperty('--placeholder-color', hex);
    el.classList.add('has-color');
  }

  /**
   * Coarse-bucket the pixels and pick the most representative colour.
   * Near-transparent pixels are ignored. Pure white/black extremes are demoted
   * (they're usually backgrounds) but still win if nothing else is present, so a
   * white-on-white product shot still yields a white-ish placeholder. Buckets are
   * scored by population weighted by saturation, so a colourful subject beats a
   * large flat backdrop. The winning bucket is then averaged for a precise hex.
   */
  function extract(img) {
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

    var data = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
    var buckets = {};
    var fallback = { r: 0, g: 0, b: 0, n: 0 }; // average of all non-transparent px

    for (var i = 0; i < data.length; i += 4) {
      var a = data[i + 3];
      if (a < 125) continue;
      var r = data[i], g = data[i + 1], b = data[i + 2];

      fallback.r += r; fallback.g += g; fallback.b += b; fallback.n++;

      var max = Math.max(r, g, b), min = Math.min(r, g, b);
      var sat = max === 0 ? 0 : (max - min) / max;
      var extreme = (r > 243 && g > 243 && b > 243) || (r < 12 && g < 12 && b < 12);

      // 4 bits per channel -> 4096 possible buckets.
      var key = (r >> 4) + ',' + (g >> 4) + ',' + (b >> 4);
      var bucket = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, n: 0, score: 0 });
      bucket.r += r; bucket.g += g; bucket.b += b; bucket.n++;
      // saturation makes vivid colours outweigh flat backdrops; extremes demoted.
      bucket.score += (1 + sat * 3) * (extreme ? 0.05 : 1);
    }

    // Mostly-transparent images (logos, wordmarks) would take their glyph colour
    // as the placeholder and then render invisibly against it, since the
    // .has-color background persists after load. Keep the shimmer instead.
    if (!fallback.n || fallback.n < (data.length / 4) * 0.6) return null;

    var best = null;
    for (var k in buckets) {
      if (!best || buckets[k].score > best.score) best = buckets[k];
    }

    var src = best && best.score > 0 ? best : fallback;
    var rr = Math.round(src.r / src.n);
    var gg = Math.round(src.g / src.n);
    var bb = Math.round(src.b / src.n);
    return '#' + ((1 << 24) + (rr << 16) + (gg << 8) + bb).toString(16).slice(1);
  }

  function process(el) {
    el.setAttribute('data-color-done', '');
    var url = el.dataset.colorSrc;
    if (!url) return;

    var cached = readCache(url);
    if (cached) { apply(el, cached); return; }

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = function () {
      var hex;
      try { hex = extract(img); } catch (e) { hex = null; } // CORS-tainted canvas, etc.
      if (hex) { writeCache(url, hex); apply(el, hex); }
    };
    img.onerror = function () { /* leave shimmer */ };
    img.src = url;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      process(entry.target);
    });
  }, { rootMargin: '200px 0px' });

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Re-scan content the theme editor injects, and AJAX-loaded grids / quickviews.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  window.addEventListener('dominant-color:scan', function (e) { scan(e.detail && e.detail.root); });
})();
